import { useState } from 'react';
import { Calendar, ChevronDown } from 'lucide-react';
import { useActiveYear } from '../../contexts/ActiveYearContext';

/**
 * Year picker in the header. Every user can change THEIR OWN view — that
 * sets a personal `?year=` override. Admins set the global active year
 * separately under Settings → System.
 *
 * Shows the effective year and, when overriding, a hint that it's a
 * personal view (the banner at the top of the page reinforces this).
 */
export default function YearSwitcher() {
  const { year, globalYear, isOverriding, setOverride, clearOverride } = useActiveYear();
  const [open, setOpen] = useState(false);

  if (year === null) return null; // still loading

  // Offer a sensible range around the global year (or today's year as fallback).
  const base = globalYear ?? new Date().getFullYear();
  const options: number[] = Array.from(
    new Set([base - 2, base - 1, base, base + 1, base + 2, year]),
  )
    .filter((y) => Number.isFinite(y))
    .sort((a, b) => b - a);

  const pickYear = (next: number) => {
    setOpen(false);
    if (globalYear !== null && next === globalYear) {
      clearOverride();
    } else {
      setOverride(next);
    }
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Viewing year ${year}. Click to change.`}
        title={
          isOverriding
            ? `Personal view: ${year} (org's current year is ${globalYear})`
            : `Active conference year: ${year}`
        }
        className={`flex items-center gap-1.5 text-sm px-2 py-1 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${
          isOverriding
            ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200 hover:bg-amber-200 dark:hover:bg-amber-800'
            : 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 hover:bg-primary-100 dark:hover:bg-primary-900/50'
        }`}
      >
        <Calendar className="w-4 h-4" aria-hidden="true" />
        <span>{year}</span>
        <ChevronDown className="w-3 h-3 opacity-70" aria-hidden="true" />
      </button>

      {open && (
        <ul
          role="listbox"
          aria-label="Pick a year to view"
          className="absolute right-0 mt-1 z-50 min-w-[140px] rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg py-1"
        >
          {options.map((y) => {
            const isCurrent = y === year;
            const isGlobal = y === globalYear;
            return (
              <li key={y} role="option" aria-selected={isCurrent}>
                <button
                  type="button"
                  onClick={() => pickYear(y)}
                  className={`w-full text-left px-3 py-1.5 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between ${
                    isCurrent
                      ? 'font-semibold text-gray-900 dark:text-white'
                      : 'text-gray-700 dark:text-gray-200'
                  }`}
                >
                  <span>{y}</span>
                  {isGlobal && (
                    <span className="ml-2 text-xs uppercase tracking-wide text-primary-600 dark:text-primary-400">
                      Current
                    </span>
                  )}
                </button>
              </li>
            );
          })}
          {isOverriding && (
            <li className="border-t border-gray-200 dark:border-gray-700 mt-1">
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  clearOverride();
                }}
                className="w-full text-left px-3 py-1.5 text-sm text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-900/30"
              >
                Return to current year
              </button>
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
